import type { AvailableOption } from '@/types';

/**
 * Helper PURI per i substep a OPZIONE (fasi 1-2 LAYOUT/TECNOLOGIA). Il backend resta la sorgente di
 * verità (eleggibilità, posizioni, prezzi, stato `isAnswered`); qui si ricostruisce soltanto l'insieme
 * `options[]` da inviare a POST /answers al toggle, secondo la semantica "l'insieme SOSTITUISCE le
 * opzioni correnti del substep" (union delle risposte).
 */

/** Opzioni base/default (OptionGroupNumber === 0) = "la configurazione di partenza" (capitolato). */
export function baseOptions(options: AvailableOption[]): AvailableOption[] {
  return options.filter((o) => o.optionGroupNumber === 0);
}

/** Opzioni alternative/variante (OptionGroupNumber !== 0) = "soluzioni aggiuntive/alternative". */
export function variantOptions(options: AvailableOption[]): AvailableOption[] {
  return options.filter((o) => o.optionGroupNumber !== 0);
}

/** Insieme corrente = OptionId attualmente risposti per il substep (deciso dal backend). */
export function currentSelectedIds(options: AvailableOption[]): number[] {
  return options.filter((o) => o.isAnswered).map((o) => o.optionId);
}

/** Id delle opzioni base (per "CONFERMA" della configurazione di partenza). */
export function baseOptionIds(options: AvailableOption[]): number[] {
  return baseOptions(options).map((o) => o.optionId);
}

/**
 * Cardinalità del substep, dedotta dai dati di dominio:
 * - se almeno una variante ha posizioni (P-n) → add-on PER-POSIZIONE (multi-select indipendente);
 * - altrimenti → SCELTA-SINGOLA (radio/tab, mutuamente esclusiva).
 */
export function isPerPosition(options: AvailableOption[]): boolean {
  return variantOptions(options).some((o) => o.positions.length > 0);
}

/** true se una variante è attualmente selezionata (insieme corrente contiene una OGN!=0 risposta). */
export function hasVariantSelected(options: AvailableOption[]): boolean {
  return variantOptions(options).some((o) => o.isAnswered);
}

/**
 * Toggle di una variante per un substep PER-POSIZIONE. Ritorna il nuovo `options[]` da salvare:
 * - se la variante è già nell'insieme → la rimuove (ELIMINA);
 * - altrimenti la aggiunge (AGGIUNGI) rimuovendo eventuali opzioni che occupano una sua posizione
 *   (una sola scelta per posizione);
 * - se l'insieme risultante è vuoto → ricade sulla base (CONFERMA implicito), così il payload non è
 *   mai vuoto e il substep resta risposto a capitolato (la planimetria defaulta le posizioni scoperte).
 */
export function toggleVariant(options: AvailableOption[], variantId: number): number[] {
  const current = new Set(currentSelectedIds(options));
  const variant = options.find((o) => o.optionId === variantId);
  if (!variant) return [...current];

  if (current.has(variantId)) {
    current.delete(variantId);
  } else {
    const variantPositions = new Set(variant.positions);
    for (const o of options) {
      if (
        o.optionId !== variantId &&
        current.has(o.optionId) &&
        o.positions.some((p) => variantPositions.has(p))
      ) {
        current.delete(o.optionId);
      }
    }
    current.add(variantId);
  }

  const result = [...current];
  return result.length > 0 ? result : baseOptionIds(options);
}

/** Scelta-singola: l'insieme diventa esattamente l'opzione scelta (base o variante). */
export function selectSingle(optionId: number): number[] {
  return [optionId];
}

/** Etichetta "P-n" di una posizione. */
export function positionLabel(n: number): string {
  return `P-${n}`;
}

/**
 * Compone la caption per-posizione della card add-on, partendo dal template del BE (`copy.caption`) e
 * dai dati dell'opzione (fedele al legacy tecnologia/*.jsx):
 * - `mount` (luci): inserisce "a {mount}" prima di "nella posizione" → "Aggiungo punti luce a soffitto
 *   nella posizione:".
 * - suffisso evidenziato: la STANZA (`roomLabel` in minuscolo, per USB → "Aggiungo USB in cucina",
 *   togliendo i due punti del template "in:"), altrimenti le POSIZIONI "P-n".
 * Ritorna null se non c'è template o nessun suffisso (la card mostrerà solo il nome).
 */
export function composeCaption(
  template: string | null,
  option: AvailableOption,
): { lead: string; emphasis: string } | null {
  if (template == null) return null;

  let lead = template;
  if (option.mount) {
    lead = lead.replace(/nella posizione/i, `a ${option.mount} nella posizione`);
  }

  if (option.roomLabel) {
    // USB: "Aggiungo USB in:" → "Aggiungo USB in" + stanza (minuscolo), come il legacy.
    return { lead: lead.replace(/:\s*$/, ''), emphasis: option.roomLabel.toLowerCase() };
  }
  if (option.positions.length > 0) {
    return { lead, emphasis: option.positions.map(positionLabel).join(', ') };
  }
  return null;
}

/**
 * Estrae un eventuale warning dal testo dell'opzione ("Attenzione! Per ragioni tecniche…"). Se il
 * testo non contiene un avviso, ritorna null (l'avviso va omesso, come da specifica AS-IS).
 */
export function extractWarning(description: string | null): string | null {
  if (description == null) return null;
  return /attenzione|ragioni tecniche/i.test(description) ? description : null;
}
